// Firebase initialization + FCM device-token registration.
//
// Public API:
//   - initFcm({ apiBase, getAuthToken })  : Promise<{ token, supported }>
//     1. Initialises the SDK (idempotent)
//     2. Registers /firebase-messaging-sw.js as the dedicated push SW
//     3. Requests Notification permission
//     4. Calls getToken() with the VAPID key
//     5. POSTs the token to /api/notifications/device-tokens
//   - onForegroundMessage(handler)        : unsubscribe
//
// All values come from REACT_APP_FIREBASE_* env vars — see /app/frontend/.env.
import { initializeApp, getApps } from "firebase/app";
import { getMessaging, getToken, onMessage, isSupported, deleteToken } from "firebase/messaging";

// Keep these public web settings aligned with firebase-messaging-sw.js. The
// Firebase web configuration is not a credential; the server's Admin key stays
// private and is configured separately on the backend.
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY || "AIzaSyBF2RzLRlJ-AX2IVsQ7kJS5WD9i40hcz9w",
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || "icaard-7e41e.firebaseapp.com",
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || "icaard-7e41e",
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || "icaard-7e41e.firebasestorage.app",
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID || "725785648373",
  appId: process.env.REACT_APP_FIREBASE_APP_ID || "1:725785648373:web:d03a37bdf0fb6631dc39bc",
};
const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;

let _app = null;
let _messaging = null;
let _swReg = null;

const getApp = () => {
  if (_app) return _app;
  _app = getApps().find((app) => app.options.projectId === firebaseConfig.projectId) || initializeApp(firebaseConfig);
  return _app;
};

const supported = async () => {
  try { return await isSupported(); } catch { return false; }
};

const ensureSW = async () => {
  if (_swReg) return _swReg;
  if (!("serviceWorker" in navigator)) throw new Error("Service workers unsupported");
  // Register at site root so its scope covers the entire app.
  _swReg = await navigator.serviceWorker.register("/firebase-messaging-sw.js", { scope: "/" });
  return _swReg;
};

export const getFcmStatus = async () => {
  if (!(await supported())) return { supported: false, permission: "unsupported" };
  return { supported: true, permission: typeof Notification !== "undefined" ? Notification.permission : "default" };
};

export const initFcm = async ({ apiBase, getAuthToken } = {}) => {
  const isOk = await supported();
  if (!isOk) return { token: null, supported: false, reason: "browser_unsupported" };
  if (typeof Notification === "undefined") return { token: null, supported: false, reason: "notification_api_missing" };
  // Permission flow
  if (Notification.permission === "denied") return { token: null, supported: true, reason: "denied" };
  if (Notification.permission !== "granted") {
    const perm = await Notification.requestPermission();
    if (perm !== "granted") return { token: null, supported: true, reason: "not_granted" };
  }
  let token;
  try {
    const app = getApp();
    _messaging = _messaging || getMessaging(app);
    const reg = await ensureSW();
    const options = { serviceWorkerRegistration: reg };
    if (VAPID_KEY) options.vapidKey = VAPID_KEY;
    token = await getToken(_messaging, options);
  } catch (err) {
    console.warn("[fcm] getToken failed:", err);
    return { token: null, supported: true, reason: "getToken_failed", error: String(err) };
  }
  if (!token) return { token: null, supported: true, reason: "no_token" };
  const jwt = getAuthToken?.();
  if (!jwt) return { token: null, supported: true, reason: "sign_in_required" };
  try {
    const base = (apiBase || window.location.origin).replace(/\/+$/, "");
    const response = await fetch(`${base}/api/notifications/device-tokens`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${jwt}` },
      body: JSON.stringify({
        fcm_token: token,
        device_type: window.matchMedia("(display-mode: standalone)").matches ? "pwa" : "web",
        user_agent: (navigator.userAgent || "").slice(0, 300),
      }),
    });
    if (!response.ok) return { token: null, supported: true, reason: "token_registration_failed", status: response.status };
    const registration = await response.json().catch(() => ({}));
    if (registration.push_ready === false) return { token, registered: true, pushReady: false, supported: true };
  } catch (e) {
    console.warn("[fcm] token registration failed:", e);
    return { token: null, supported: true, reason: "token_registration_failed" };
  }
  return { token, registered: true, pushReady: true, supported: true };
};

export const onForegroundMessage = (handler) => {
  if (!_messaging) {
    try { _messaging = getMessaging(getApp()); } catch { return () => {}; }
  }
  return onMessage(_messaging, handler);
};

export const revokeFcmToken = async () => {
  try {
    if (!_messaging) _messaging = getMessaging(getApp());
    await deleteToken(_messaging);
  } catch (e) { /* best-effort */ }
};
